/**
 * AppShell - Shared application shell for both web and mobile platforms
 *
 * Contains all shared infrastructure:
 * - BrowserRouter
 * - AuthProvider
 * - SettingsProvider (user settings with statistics)
 * - Toaster configuration
 * - Route structure with ProtectedRoute and OnboardingGuard
 *
 * Platform-specific page components are injected via the routes config prop.
 */
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "../contexts/AuthContext";
import { SettingsProvider } from "../contexts/SettingsContext";
import { FeatureAccessProvider } from "../contexts/FeatureAccessProvider";
import { ProtectedRoute } from "./ProtectedRoute";
import { OnboardingGuard } from "./OnboardingGuard";
import { FeedbackWidget } from "./feedback/FeedbackWidget";
import { NavBar } from "./NavBar";
import { NetworkStatusBanner } from "./NetworkStatusBanner";
import type { PlatformRouteConfig } from "../types/platformRoutes";
import "../styles/layout.css";
import "../styles/navigation.css";
import "../styles/financial.css";
import "../styles/projection.css";
import "../styles/score.css";
import "../styles/readiness.css";
import "../styles/scenario.css";
import "../styles/toast.css";
import "../styles/forms.css";
import "../styles/feedback.css";
import "../styles/skeleton.css";
import "../styles/data-table.css";
import "../styles/onboarding.css";
import "../styles/recommendations.css";
import "../styles/legal.css";
import "../styles/trial.css";
import "../styles/statistics.css";
import "../styles/what-if.css";
import "../styles/dashboard.css";
import "../styles/marketing.css";

import SignIn from "../pages/SignIn";
import Pricing from "../pages/Pricing";
import OnboardingWelcome from "../pages/OnboardingWelcome";
import IntakeInterviewPage from "../pages/onboarding/IntakeInterviewPage";
import QuickPeekPage from "../pages/onboarding/QuickPeekPage";
import DataEntryPage from "../pages/onboarding/DataEntryPage";
import OnboardingCelebrationPage from "../pages/onboarding/OnboardingCelebrationPage";
import TermsOfService from "../pages/TermsOfService";
import PrivacyPolicy from "../pages/PrivacyPolicy";
import AccessibilityStatement from "../pages/AccessibilityStatement";
import ProjectionsWeb from "../pages/Projections.web";

interface AppShellProps {
  routes: PlatformRouteConfig;
}

export function AppShell({ routes }: AppShellProps) {
  const {
    Dashboard,
    Settings,
    Profile,
    InvestableAssets,
    RealEstateAssets,
    Liabilities,
    Income,
    Expenses,
    Assumptions,
    OnboardingPage,
  } = routes;

  return (
    <BrowserRouter>
      <AuthProvider>
        <SettingsProvider>
          <FeatureAccessProvider>
            {/* Skip link — first focusable element for keyboard users */}
            <a href="#main-content" className="skip-link">
              Skip to main content
            </a>
            <Toaster
              position="top-center"
              toastOptions={{
                duration: 4000,
                style: {
                  background: "#363636",
                  color: "#fff",
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: "#10b981",
                    secondary: "#fff",
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: "#ef4444",
                    secondary: "#fff",
                  },
                },
              }}
            />
            <main
              id="main-content"
              role="main"
              className="app-content-with-nav"
            >
              <Routes>
                <Route path="/signin" element={<SignIn />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route
                  path="/accessibility"
                  element={<AccessibilityStatement />}
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                {/* Onboarding routes (FS-017 adaptive + legacy category flow) */}
                <Route
                  path="/onboarding/welcome"
                  element={
                    <ProtectedRoute>
                      <OnboardingGuard>
                        <OnboardingWelcome />
                      </OnboardingGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/onboarding/intake"
                  element={
                    <ProtectedRoute>
                      <OnboardingGuard>
                        <IntakeInterviewPage />
                      </OnboardingGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/onboarding/quick-peek"
                  element={
                    <ProtectedRoute>
                      <OnboardingGuard>
                        <QuickPeekPage />
                      </OnboardingGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/onboarding/data-entry"
                  element={
                    <ProtectedRoute>
                      <OnboardingGuard>
                        <DataEntryPage />
                      </OnboardingGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/onboarding/celebration"
                  element={
                    <ProtectedRoute>
                      <OnboardingGuard>
                        <OnboardingCelebrationPage />
                      </OnboardingGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/onboarding/:category"
                  element={
                    <ProtectedRoute>
                      <OnboardingGuard>
                        <OnboardingPage />
                      </OnboardingGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projections"
                  element={
                    <ProtectedRoute>
                      <ProjectionsWeb />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/investableAssets"
                  element={
                    <ProtectedRoute>
                      <InvestableAssets />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/realEstateAssets"
                  element={
                    <ProtectedRoute>
                      <RealEstateAssets />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/liabilities"
                  element={
                    <ProtectedRoute>
                      <Liabilities />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/income"
                  element={
                    <ProtectedRoute>
                      <Income />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/expenses"
                  element={
                    <ProtectedRoute>
                      <Expenses />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/assumptions"
                  element={
                    <ProtectedRoute>
                      <Assumptions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/"
                  element={<Navigate to="/dashboard" replace />}
                />
                <Route
                  path="*"
                  element={<Navigate to="/dashboard" replace />}
                />
              </Routes>
            </main>
            <NavBar />
            <NetworkStatusBanner />
            <FeedbackWidget />
          </FeatureAccessProvider>
        </SettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
